import React, { useCallback, useEffect, useState } from 'react';
import { TrendingDown } from 'lucide-react';
import { apiClient } from '../packages/api-client/index';
import { StateView, Table } from '../packages/ui';
import { ChartFigure } from './ChartFigure';

export interface BurndownPoint {
  date: string;     // YYYY-MM-DD
  open: number;
  closed: number;
}

/** Report periods; 0 = everything the API returned. */
const PERIODS = [
  { days: 0, label: 'All time' },
  { days: 7, label: '7 days' },
  { days: 14, label: '14 days' },
  { days: 30, label: '30 days' },
];

/** YYYY-MM-DD of `days` ago in local time, for filtering the API's day keys. */
function sinceDay(days: number, now = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Inline SVG chart; no chart dependency. Numbers come only from
// GET /projects/{id}/burndown (scoped to what the caller can see); the period
// filter only narrows the returned days.
export function BurndownChart({ projectId }: { projectId: string }) {
  const [all, setAll] = useState<BurndownPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [period, setPeriod] = useState(0);

  const load = useCallback(() => {
    setLoading(true); setError(false);
    apiClient.get<{ data: BurndownPoint[] }>(`/projects/${projectId}/burndown`)
      .then(r => setAll(r.data.data ?? []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [projectId]);
  useEffect(load, [load]);

  const title = <><TrendingDown size={16} aria-hidden="true" /> Burndown</>;
  if (loading) return <StateView state="loading" label="Loading burndown" />;
  if (error) return <StateView state="error" title="Burndown couldn't be loaded" message="The chart data request failed." onRetry={load} />;

  const from = period ? sinceDay(period) : '';
  const data = all.filter(p => p.date >= from);
  const periodChips = (
    <span className="rpt-chips" role="group" aria-label="Burndown period">
      {PERIODS.map(p => (
        <button key={p.days} type="button" className={`period-chip${period === p.days ? ' active' : ''}`}
          aria-pressed={period === p.days} onClick={() => setPeriod(p.days)}>{p.label}</button>
      ))}
    </span>
  );

  const note = <p className="rpt-muted">Each day counts the tasks created that day, split by their current status (open, or done/cancelled).</p>;
  if (data.length === 0) return (
    <ChartFigure title={title} summary="No burndown data" actions={periodChips}
      chart={<StateView state="empty" title={all.length ? 'No tasks were created in this period' : 'No burndown data yet'}
        message={all.length ? 'Choose a longer period.' : 'Create tasks to see the burndown.'} />}
      table={<StateView state="empty" title="No rows for this period" />} />
  );

  const W = 560, H = 220, PAD = 40;
  const n = data.length;
  const maxOpen = Math.max(...data.map(d => Math.max(d.open, d.closed)), 1);
  const x = (i: number) => (n === 1 ? W / 2 : PAD + (i / (n - 1)) * (W - PAD * 2));
  const y = (v: number) => PAD + (1 - v / maxOpen) * (H - PAD * 2);
  const path = (k: 'open' | 'closed') => data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(d[k]).toFixed(1)}`).join(' ');
  const yTicks = Array.from(new Set(Array.from({ length: 5 }, (_, i) => Math.round((i / 4) * maxOpen))));
  const xTicks = data.filter((_, i) => i % Math.max(1, Math.floor(n / 6)) === 0 || i === n - 1);
  const last = data[n - 1];

  return (
    <div className="burndown-card">
      <ChartFigure title={title} actions={periodChips}
        summary={`Burndown from ${data[0].date} to ${last.date}: on ${last.date}, ${last.open} open and ${last.closed} closed.`}
        chart={<>
          <div className="burndown-legend" aria-hidden="true">
            <span className="burndown-legend-open">● Open</span>
            <span className="burndown-legend-closed">● Done or cancelled</span>
          </div>
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxHeight: 240 }} aria-hidden="true">
            {yTicks.map(v => (
              <g key={v}>
                <line x1={PAD} y1={y(v)} x2={W - PAD} y2={y(v)} strokeDasharray="4 4" style={{ stroke: 'var(--border)' }} />
                <text x={PAD - 6} y={y(v) + 4} textAnchor="end" fontSize={10} style={{ fill: 'var(--ink-muted)' }}>{v}</text>
              </g>
            ))}
            {xTicks.map(d => (
              <text key={d.date} x={x(data.indexOf(d))} y={H - 6} textAnchor="middle" fontSize={9} style={{ fill: 'var(--ink-muted)' }}>{d.date.slice(5)}</text>
            ))}
            <path d={path('open')} fill="none" strokeWidth={2.5} strokeLinejoin="round" style={{ stroke: 'var(--accent)' }} />
            <path d={path('closed')} fill="none" strokeWidth={2.5} strokeLinejoin="round" style={{ stroke: 'var(--ok)' }} />
            <circle cx={x(n - 1)} cy={y(last.open)} r={4} style={{ fill: 'var(--accent)' }} />
            <circle cx={x(n - 1)} cy={y(last.closed)} r={4} style={{ fill: 'var(--ok)' }} />
          </svg>
        </>}
        table={<Table caption="Burndown by day" density="compact" rowKey={r => String(r.date)}
          columns={[{ key: 'date', header: 'Day', rowHeader: true }, { key: 'open', header: 'Open' }, { key: 'closed', header: 'Done or cancelled' }]}
          data={data as unknown as Record<string, unknown>[]} />}
      />
      {note}
    </div>
  );
}
